/**
 * Font loading utilities for optimized font performance
 */

export interface FontLoadingConfig {
  family: string
  weight?: string
  style?: string
  timeout?: number
}

/**
 * Load a font with timeout and error handling
 */
export async function loadFont(config: FontLoadingConfig): Promise<boolean> {
  const { family, weight = '', style = 'normal', timeout = 3000 } = config
  
  if (!('fonts' in document)) {
    // Fallback for browsers without Font Loading API
    return true
  }
  
  try {
    const font = new FontFace(family, `url(/fonts/${family}-${weight}.woff2)`, {
      weight,
      style,
      display: 'swap'
    })
    
    // Add timeout to prevent hanging
    const timeoutPromise = new Promise<never>((_, reject) => 
      setTimeout(() => reject(new Error('Font loading timeout')), timeout)
    )
    
    const loadedFont = await Promise.race([font.load(), timeoutPromise])
    document.fonts.add(loadedFont)
    
    return true
  } catch {
    return false
  }
}

/**
 * Preload critical fonts for better performance
 */
export function preloadCriticalFonts(locale: string): void {
  const criticalFonts = [
    { family: 'AeonikPro', weight: 'Regular' },
    { family: 'AeonikPro', weight: 'Medium' }
  ]
  
  // Add locale-specific critical fonts
  const localeSpecificFonts: Record<string, FontLoadingConfig[]> = {
    'ar': [{ family: 'NotoSansArabic', weight: '400' }],
    'zh': [{ family: 'NotoSansSC', weight: '400' }],
    'zh-TW': [{ family: 'NotoSansTC', weight: '400' }],
    'ja': [{ family: 'NotoSansJP', weight: '400' }],
    'ko': [{ family: 'NotoSansKR', weight: '400' }],
    'he': [{ family: 'NotoSansHebrew', weight: '400' }]
  }
  
  const fontsToLoad = [
    ...criticalFonts,
    ...(localeSpecificFonts[locale] || [])
  ]
  
  // Load fonts in parallel
  Promise.allSettled(
    fontsToLoad.map(font => loadFont(font))
  ).then(results => {
    const successCount = results.filter(r => r.status === 'fulfilled' && r.value).length
    console.warn(`Loaded ${successCount}/${fontsToLoad.length} critical fonts`)
    
    // Mark fonts as loaded for CSS transitions
    document.documentElement.classList.add('fonts-loaded')
  })
}

/**
 * Check if a font is available
 */
export function isFontAvailable(fontFamily: string): boolean {
  if (!('fonts' in document)) {
    return false
  }
  
  return document.fonts.check(`12px "${fontFamily}"`)
}

/**
 * Get optimal font stack for locale
 */
export function getOptimalFontStack(locale: string): string {
  const baseFallback = 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  
  const localeSpecificStacks: Record<string, string> = {
    'ar': `"Noto Sans Arabic", ${baseFallback}`,
    'zh': `"Noto Sans SC", ${baseFallback}`,
    'zh-TW': `"Noto Sans TC", ${baseFallback}`,
    'ja': `"Noto Sans JP", ${baseFallback}`,
    'ko': `"Noto Sans KR", ${baseFallback}`,
    'he': `"Noto Sans Hebrew", ${baseFallback}`
  }
  
  return localeSpecificStacks[locale] || baseFallback
}

/**
 * Font loading performance metrics
 */
export interface FontMetrics {
  loadTime: number
  success: boolean
  fallbackUsed: boolean
}

export class FontPerformanceMonitor {
  private metrics: Map<string, FontMetrics> = new Map()
  
  startTracking(fontFamily: string): void {
    const startTime = performance.now()
    
    if ('fonts' in document) {
      document.fonts.ready.then(() => {
        const endTime = performance.now()
        const isLoaded = this.isFontLoaded(fontFamily)
        
        this.metrics.set(fontFamily, {
          loadTime: endTime - startTime,
          success: isLoaded,
          fallbackUsed: !isLoaded
        })
      })
    }
  }
  
  private isFontLoaded(fontFamily: string): boolean {
    return document.fonts.check(`12px "${fontFamily}"`)
  }
  
  getMetrics(): Map<string, FontMetrics> {
    return new Map(this.metrics)
  }
  
  reportMetrics(): void {
    console.group('Font Loading Performance')
    this.metrics.forEach((metrics, font) => {
      console.log(`${font}:`, {
        'Load Time': `${metrics.loadTime.toFixed(2)}ms`,
        'Success': metrics.success,
        'Fallback Used': metrics.fallbackUsed
      })
    })
    console.groupEnd()
  }
}